import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SEO from "../components/SEO";
import {
  BookOpen, Target, MapPin, Bus, Globe, GraduationCap,
  Microscope, Trophy, ChevronRight, ChevronLeft, Phone,
  Play, Quote, ArrowRight, CheckCircle, X, Send, Loader,
  Award, Users, Volume2, VolumeX
} from "lucide-react";
import {
  schoolInfo, stats, tipsEdgeFeatures,
  testimonials
} from "../data/mock";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import useCampusesHook from "../hooks/useCampuses";
import useGrades from "../hooks/useGrades";

const CRM = process.env.REACT_APP_CRM_BACKEND;
const CRM_KEY = process.env.REACT_APP_CRM_API_KEY;
const BE = process.env.REACT_APP_BACKEND_URL;
const iconMap = { BookOpen, Target, MapPin, Bus, Globe, GraduationCap, Microscope, Trophy };

// -- Counter Hook --
const useCountUp = (target, duration = 2000, active = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, active]);
  return count;
};

// -- InView Hook --
const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
};

// ==================== HERO SECTION ====================
const HeroSection = ({ campusList, campusNames }) => {
  const [form, setForm] = useState({ name: "", childName: "", phone: "", campus: "", campusId: "", grade: "" });
  const gradeList = useGrades(form.campusId);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.campus) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`${CRM}/api/public/leads`, {
        parent_name: form.name,
        child_name: form.childName || undefined,
        phone: form.phone,
        campus_preference: form.campus,
        grade_applying: form.grade || undefined,
        source: "Website",
        city: "Bengaluru",
        school: "TIPS Bengaluru",
      }, { headers: { "X-API-Key": CRM_KEY } });
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong. Please call us at 7619305034.");
    } finally {
      setLoading(false);
    }
  };

  const [heroMuted, setHeroMuted] = useState(true);
  const heroVideoRef = useRef(null);

  const toggleHeroMute = () => {
    setHeroMuted((m) => {
      if (heroVideoRef.current) heroVideoRef.current.muted = !m;
      return !m;
    });
  };

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Hero Video Background */}
      <video
        ref={heroVideoRef}
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        style={{ zIndex: 0 }}
      >
        <source src="/campus_tour.mp4" type="video/mp4" />
      </video>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-[#1A0505] opacity-70" style={{ zIndex: 1 }}></div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0b1437]/80 via-[#0b1437]/50 to-transparent" style={{ zIndex: 1 }}></div>

      {/* Mute/Unmute toggle — bottom right */}
      <button
        onClick={toggleHeroMute}
        className="absolute bottom-6 right-6 z-10 bg-black/50 hover:bg-black/70 border border-white/20 text-white rounded-full p-2.5 transition-all"
        title={heroMuted ? "Unmute video" : "Mute video"}
      >
        {heroMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
      </button>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full" style={{ zIndex: 2 }}>
        <div className="flex flex-col lg:flex-row items-center justify-between gap-10">
          {/* Left content */}
          <div className="lg:w-3/5 text-white">
            <div className="inline-flex items-center gap-2 bg-[#B41C2C]/20 border border-[#B41C2C]/30 text-[#D97481] text-xs font-semibold px-4 py-1.5 rounded-full mb-6 tracking-wide">
              Admissions Open 2026–27  •  Nursery to Grade XII
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Fulfilling <span className="text-[#C83C4A]">Dreams,</span><br />
              One at a Time
            </h1>
            <p className="text-lg text-gray-300 mb-3">CBSE Affiliated · Affiliation No. 830884</p>
            <p className="text-base text-gray-400 max-w-xl mb-8 leading-relaxed">
              World-class CBSE curriculum across 6 campuses in Bengaluru, shaping globally-minded leaders for 20+ years.
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              <span className="inline-flex items-center gap-1.5 bg-orange-500/20 border border-orange-400/30 text-orange-300 text-xs font-bold px-3 py-1.5 rounded-full">CBSE Affiliated</span>
              <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 text-gray-300 text-xs font-bold px-3 py-1.5 rounded-full">Nursery to Grade XII</span>
              <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 text-gray-300 text-xs font-bold px-3 py-1.5 rounded-full">6 Campuses · Bengaluru</span>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link to="/admissions" className="bg-[#B41C2C] hover:bg-[#9B1522] text-white font-semibold px-7 py-3 rounded-lg transition-colors flex items-center gap-2">
                Apply Now <ArrowRight size={16} />
              </Link>
              <Link to="/campuses" className="border-2 border-white/40 hover:border-white text-white font-semibold px-7 py-3 rounded-lg transition-colors hover:bg-white/10">
                Explore Campuses
              </Link>
            </div>
          </div>

          {/* Quick Enquiry Form */}
          <div className="lg:w-2/5 w-full max-w-sm">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="bg-[#9B1522] px-5 py-4">
                <h3 className="text-white font-bold text-base" style={{ fontFamily: "'Poppins', sans-serif" }}>Book a Free Counselling</h3>
                <p className="text-[#F8C5C9] text-xs mt-0.5">Our team will get back within 24 hours</p>
              </div>
              <div className="p-5">
                {!submitted ? (
                  <form onSubmit={handleSubmit} className="space-y-3.5">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Parent / Guardian Name *</label>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Your full name"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Child's Name</label>
                      <input
                        type="text"
                        value={form.childName}
                        onChange={(e) => setForm({ ...form, childName: e.target.value })}
                        placeholder="Child's full name"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Mobile Number *</label>
                      <input
                        type="tel"
                        required
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="10-digit mobile number"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Select Campus *</label>
                      <Select onValueChange={(v) => {
                        const c = campusList.find((x) => x.name === v);
                        setForm(prev => ({ ...prev, campus: v, campusId: c?.id || "", grade: "" }));
                      }}>
                        <SelectTrigger className="w-full border-gray-200 text-sm h-9">
                          <SelectValue placeholder="Choose campus" />
                        </SelectTrigger>
                        <SelectContent>
                          {(campusNames || []).map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Grade Applying For</label>
                      <Select
                        disabled={!form.campus}
                        onValueChange={(v) => setForm(prev => ({ ...prev, grade: v }))}
                      >
                        <SelectTrigger className="w-full border-gray-200 text-sm h-9" disabled={!form.campus}>
                          <SelectValue placeholder={form.campus ? "Select grade" : "Select campus first"} />
                        </SelectTrigger>
                        <SelectContent>
                          {(gradeList.length > 0 ? gradeList : ["Nursery","LKG","UKG","Grade I","Grade II","Grade III","Grade IV","Grade V","Grade VI","Grade VII","Grade VIII","Grade IX","Grade X","Grade XI","Grade XII"]).map((g) => (
                            <SelectItem key={g} value={g}>{g}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#9B1522] hover:bg-[#87111E] text-white font-semibold py-2.5 rounded-lg text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                    >
                      {loading ? <><Loader size={14} className="animate-spin" /> Submitting...</> : <><Send size={14} /> Submit Enquiry</>}
                    </button>
                    {error && <p className="text-xs text-red-600 text-center mt-1">{error}</p>}
                  </form>
                ) : (
                  <div className="py-6 text-center">
                    <div className="w-14 h-14 bg-[#FEE7E9] rounded-full flex items-center justify-center mx-auto mb-3">
                      <CheckCircle size={28} className="text-[#B41C2C]" />
                    </div>
                    <h4 className="font-bold text-gray-800 mb-1">Enquiry Submitted!</h4>
                    <p className="text-sm text-gray-500">Our admissions team will contact you within 24 hours.</p>
                    <button onClick={() => { setSubmitted(false); setForm({ name: "", childName: "", phone: "", campus: "", grade: "" }); }} className="mt-4 text-sm text-[#B41C2C] hover:text-[#9B1522] font-medium">Submit another enquiry</button>
                  </div>
                )}
                <p className="text-center text-xs text-gray-400 mt-3">Or call <a href="tel:7619305034" className="text-[#B41C2C] font-semibold">7619305034</a></p>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/50 text-xs animate-bounce">
          <span>Scroll</span>
          <div className="w-px h-8 bg-white/30"></div>
        </div>
      </div>
    </section>
  );
};

// ==================== STATS SECTION ====================
const StatCounter = ({ value, suffix, label, active }) => {
  const count = useCountUp(value, 2200, active);
  return (
    <div className="text-center">
      <div className="text-5xl font-bold text-white mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
        {count}{suffix}
      </div>
      <div className="text-[#D97481] text-sm font-medium tracking-wide">{label}</div>
    </div>
  );
};

const StatsSection = () => {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className="bg-[#1A0505] py-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
          {stats.map((stat) => <StatCounter key={stat.id} {...stat} active={inView} />)}
        </div>
        <div className="mt-10 flex flex-wrap justify-center gap-6">
          {[
            { label: "CBSE Affiliated", sub: "Affiliation No. 830884" },
            { label: "Forbes India", sub: "Great Place to Study" },
            { label: "20+ Years", sub: "of Academic Excellence" },
            { label: "6 Campuses", sub: "Across Bengaluru" }
          ].map((b) => (
            <div key={b.label} className="flex items-center gap-3 border border-gray-700 rounded-xl px-5 py-3">
              <div className="w-2 h-2 rounded-full bg-[#B41C2C]"></div>
              <div>
                <div className="text-white text-sm font-semibold">{b.label}</div>
                <div className="text-gray-500 text-xs">{b.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== ACADEMICS SECTION ====================
const AcademicsSection = () => {
  const [ref, inView] = useInView();
  const cbseFeatures = [
    { icon: BookOpen, title: "Conceptual Clarity", desc: "In-depth understanding over rote learning, building a strong foundation from Nursery to Grade XII." },
    { icon: Award, title: "National Benchmarks", desc: "Consistently achieving top scores. Internal assessments and feedback make board exams relatively easier." },
    { icon: Users, title: "Holistic Development", desc: "Beyond academics — sports, arts, life skills, and character education woven into the curriculum." },
    { icon: CheckCircle, title: "Nursery to Grade XII", desc: "Seamless academic progression across all grades under one roof, with dedicated faculty at every stage." },
  ];
  return (
    <section ref={ref} className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="text-[#B41C2C] text-sm font-bold uppercase tracking-widest">Our Academics</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
            CBSE Excellence<br />Across All Campuses
          </h2>
          <p className="mt-4 text-gray-500 max-w-xl mx-auto">
            All 6 TIPS Bengaluru campuses follow the Central Board of Secondary Education curriculum —
            delivering a strong, consistent academic experience from Nursery to Grade XII.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-700 text-sm font-bold px-5 py-2 rounded-full">
            <Award size={15} />
            CBSE Affiliated · Affiliation No. 830884
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {cbseFeatures.map((f) => (
            <div key={f.title} className="bg-orange-50 border border-orange-100 rounded-2xl p-6 hover:shadow-md transition-shadow">
              <f.icon size={28} className="text-[#B41C2C] mb-4" />
              <h3 className="font-bold text-gray-900 mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>{f.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/academics" className="inline-flex items-center gap-2 text-[#B41C2C] font-semibold hover:text-[#9B1522] group">
            Explore Our Academic Programme <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ==================== TIPS EDGE SECTION ====================
const TipsEdgeSection = () => {
  const scrollRef = useRef(null);
  const scroll = (dir) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir * 280, behavior: "smooth" });
  };
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-[#B41C2C] text-sm font-bold uppercase tracking-widest">The TIPS Edge</span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>What Sets Us Apart</h2>
          </div>
          <div className="hidden sm:flex gap-2">
            <button onClick={() => scroll(-1)} className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-[#B41C2C] hover:text-[#B41C2C] transition-colors"><ChevronLeft size={18} /></button>
            <button onClick={() => scroll(1)} className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-[#B41C2C] hover:text-[#B41C2C] transition-colors"><ChevronRight size={18} /></button>
          </div>
        </div>
        <p className="text-gray-500 text-sm mb-2 sm:hidden">Swipe to explore →</p>
        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide"
          style={{ scrollSnapType: "x mandatory" }}
        >
          {tipsEdgeFeatures.map((f) => {
            const Icon = iconMap[f.icon] || BookOpen;
            return (
              <div
                key={f.id}
                className="flex-shrink-0 w-60 bg-white rounded-2xl p-6 border border-gray-100 hover:border-[#F8C5C9] hover:shadow-lg transition-all duration-300 group"
                style={{ scrollSnapAlign: "start" }}
              >
                <div className="w-12 h-12 bg-[#FDF5F6] rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#FEE7E9] transition-colors">
                  <Icon size={22} className="text-[#B41C2C]" />
                </div>
                <h4 className="font-bold text-gray-900 text-sm mb-2">{f.title}</h4>
                <p className="text-gray-500 text-xs leading-relaxed">{f.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ==================== CAMPUS TOUR SECTION ====================
const TOUR_VIDEO_URL = "/campus_tour.mp4";

const CampusTourSection = () => {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  const handlePlay = () => {
    setPlaying(true);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
    }
  };

  const toggleMute = () => {
    setMuted((m) => {
      if (videoRef.current) videoRef.current.muted = !m;
      return !m;
    });
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-[#B41C2C] text-sm font-bold uppercase tracking-widest">Virtual Campus Tour</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>See TIPS in Action</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-3">
            <div
              className="relative rounded-2xl overflow-hidden bg-gray-900 shadow-2xl"
              style={{ aspectRatio: "16/9" }}
            >
              <video
                ref={videoRef}
                src={TOUR_VIDEO_URL}
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
              />
              {!playing && (
                <div
                  className="absolute inset-0 bg-black/30 flex items-center justify-center cursor-pointer group"
                  onClick={handlePlay}
                >
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                    <Play size={24} className="text-[#9B1522] ml-1" />
                  </div>
                  <div className="absolute bottom-4 left-4 bg-black/60 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                    FEATURED · Campus Tour
                  </div>
                </div>
              )}
              {playing && (
                <>
                  {/* Mute / Unmute */}
                  <button
                    onClick={toggleMute}
                    className="absolute bottom-3 left-3 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 z-10 transition-all"
                    title={muted ? "Enable sound" : "Mute"}
                  >
                    {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                  </button>
                  {/* Close / back to preview */}
                  <button
                    onClick={() => { setPlaying(false); setMuted(true); if (videoRef.current) videoRef.current.muted = true; }}
                    className="absolute top-3 right-3 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5 z-10"
                  >
                    <X size={16} />
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="lg:col-span-2">
            <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>Experience It Live</h3>
            <h4 className="text-lg font-semibold text-gray-700 mb-3">Book a Campus Tour</h4>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">Walk through our labs, classrooms, and sports facilities. Meet our educators and experience the TIPS difference for yourself.</p>
            <ul className="space-y-2.5 mb-7">
              {["Mon–Sat, 9 AM – 1 PM", "Free guided tours available", "Meet Principal on request", "All 6 campuses accessible"].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-gray-700">
                  <CheckCircle size={15} className="text-[#B41C2C]" /> {item}
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={`https://wa.me/917619305034?text=Hi!%20I'd%20like%20to%20book%20a%20campus%20tour%20at%20TIPS%20Bengaluru.`}
                target="_blank" rel="noreferrer"
                className="bg-[#9B1522] hover:bg-[#87111E] text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                Book a Campus Tour
              </a>
              <Link to="/gallery" className="border border-gray-300 hover:border-[#B41C2C] text-gray-700 hover:text-[#B41C2C] text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors text-center">
                Watch All Videos
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== CAMPUSES SECTION ====================
const CampusesSection = ({ campuses }) => {
  const curriculumColors = { CBSE: "bg-orange-100 text-orange-700", IB: "bg-purple-100 text-purple-700", Cambridge: "bg-blue-100 text-blue-700" };
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="text-[#B41C2C] text-sm font-bold uppercase tracking-widest">Our Presence</span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>6 Campuses Across Bengaluru</h2>
          </div>
          <Link to="/campuses" className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-[#9B1522] hover:text-[#87111E] transition-colors">
            View All Campuses <ArrowRight size={15} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {campuses.map((campus) => (
            <div key={campus.id}
              className={`bg-white rounded-2xl p-6 border-2 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group ${
                campus.featured ? "border-[#C83C4A] shadow-lg" : "border-gray-100"
              }`}
            >
              <Link to={`/campuses#${campus.id}`} className="block">
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 bg-[#9B1522] rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {campus.code.slice(0, 2)}
                </div>
                {campus.featured && (
                  <span className="text-xs font-bold bg-[#FDF5F6] text-[#9B1522] border border-[#F8C5C9] px-2.5 py-1 rounded-full">Flagship</span>
                )}
              </div>
              <h3 className="font-bold text-gray-900 text-base mb-1 group-hover:text-[#9B1522] transition-colors">{campus.name}</h3>
              <p className="text-[#B41C2C] text-xs font-semibold mb-2">{campus.area}</p>
              <p className="text-gray-500 text-xs leading-relaxed mb-4 line-clamp-2">{campus.address}</p>
              </Link>
              <div className="flex items-center justify-between">
                <div className="flex gap-1.5 flex-wrap">
                  {campus.curricula.map((c) => (
                    <span key={c} className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${curriculumColors[c] || "bg-gray-100 text-gray-600"}`}>{c}</span>
                  ))}
                </div>
                <a href={`tel:${campus.phone}`} className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#B41C2C] transition-colors">
                  <Phone size={11} /> {campus.phone}
                </a>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-8 sm:hidden">
          <Link to="/campuses" className="inline-flex items-center gap-2 text-sm font-semibold text-[#9B1522] hover:text-[#87111E]">
            View All Campuses <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ==================== EVENTS SECTION ====================
const CATEGORY_COLORS = {
  Academic: "bg-blue-50 text-blue-700 border-blue-200",
  Sports:   "bg-orange-50 text-orange-700 border-orange-200",
  Cultural: "bg-purple-50 text-purple-700 border-purple-200",
  Holiday:  "bg-green-50 text-green-700 border-green-200",
  Admission:"bg-[#FDF5F6] text-[#9B1522] border-[#F8C5C9]",
  Admissions:"bg-[#FDF5F6] text-[#9B1522] border-[#F8C5C9]",
  Event:    "bg-gray-100 text-gray-700 border-gray-200",
  Celebration:"bg-pink-50 text-pink-700 border-pink-200",
  Workshop: "bg-teal-50 text-teal-700 border-teal-200",
  Other:    "bg-gray-100 text-gray-700 border-gray-200",
};
const CATEGORY_DOT = {
  Academic: "bg-blue-500", Sports: "bg-orange-500", Cultural: "bg-purple-500",
  Holiday: "bg-green-500", Admission: "bg-[#B41C2C]", Admissions: "bg-[#B41C2C]",
  Event: "bg-gray-400", Celebration: "bg-pink-500", Workshop: "bg-teal-500", Other: "bg-gray-400",
};

const normalizeEvent = (ev) => ({
  title:       ev.title || "",
  date:        ev.dateIso || ev.date || "",
  dateDisplay: ev.date || ev.dateIso || "",
  time:        ev.time || "",
  description: ev.description || "",
  category:    ev.type || ev.category || "Event",
  campus:      ev.campus || "All Campuses",
  image_url:   ev.imageUrl || ev.image_url || "",
  active:      ev.active !== false,
});

const EventsSection = () => {
  const [liveEvents, setLiveEvents]   = useState([]);
  const [evLoading, setEvLoading]     = useState(true);

  useEffect(() => {
    axios
      .get(`${CRM}/api/public/events/tipsblr`, { headers: { "X-API-Key": CRM_KEY } })
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          setLiveEvents(res.data.filter((e) => e.active !== false).map(normalizeEvent).slice(0, 8));
        } else {
          setLiveEvents([]); // CRM empty — no mock fallback
        }
      })
      .catch(() => setLiveEvents([]))  // CRM error — no mock fallback
      .finally(() => setEvLoading(false));
  }, []);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <span className="text-[#B41C2C] text-sm font-bold uppercase tracking-widest">School Calendar</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>Upcoming Events</h2>
        </div>
        {evLoading ? (
          <div className="flex justify-center py-8"><Loader size={28} className="animate-spin text-[#B41C2C]" /></div>
        ) : liveEvents.length === 0 ? (
          <p className="text-center text-gray-400 py-8">No upcoming events scheduled.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {liveEvents.map((ev) => {
              const colorClass = CATEGORY_COLORS[ev.category] || CATEGORY_COLORS.Other;
              const dotClass   = CATEGORY_DOT[ev.category]   || CATEGORY_DOT.Other;
              const dateObj    = ev.date ? new Date(ev.date + "T00:00:00") : null;
              return (
                <div key={ev.id} className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-md transition-shadow group">
                  {ev.image_url && (
                    <div className="h-36 overflow-hidden">
                      <img src={ev.image_url} alt={ev.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.target.parentElement.style.display = "none"; }} />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${colorClass}`}>{ev.category}</span>
                    </div>
                    <p className="text-xs font-semibold text-[#B41C2C] mb-1">
                      {ev.dateDisplay || (dateObj ? dateObj.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "")}
                      {ev.time && ` · ${ev.time}`}
                    </p>
                    <h4 className="font-bold text-gray-900 text-sm mb-2 group-hover:text-[#9B1522] transition-colors leading-snug">{ev.title}</h4>
                    <div className="flex items-center gap-1 mb-2">
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotClass}`}></div>
                      <span className="text-xs text-gray-500">{ev.campus}</span>
                    </div>
                    {ev.description && <p className="text-xs text-gray-500 leading-relaxed line-clamp-3">{ev.description}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

// ==================== TESTIMONIALS SECTION ====================
const TestimonialsSection = () => {
  const [current, setCurrent] = useState(0);
  const len = testimonials.length;
  const prev = useCallback(() => setCurrent((c) => (c - 1 + len) % len), [len]);
  const next = useCallback(() => setCurrent((c) => (c + 1) % len), [len]);

  useEffect(() => {
    const t = setInterval(next, 5000);
    return () => clearInterval(t);
  }, [next]);

  const t = testimonials[current];
  return (
    <section className="py-20 bg-[#FDF5F6]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-[#B41C2C] text-sm font-bold uppercase tracking-widest">Parent Voices</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>What Families Say About TIPS</h2>
        </div>
        <div className="relative bg-white rounded-3xl shadow-lg p-8 sm:p-12">
          <Quote size={40} className="text-[#FEE7E9] absolute top-6 left-6" />
          <div className="relative">
            <p className="text-gray-700 text-lg leading-relaxed mb-8 italic">&ldquo;{t.text}&rdquo;</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#9B1522] text-white flex items-center justify-center font-bold text-lg">{t.initial}</div>
                <div>
                  <p className="font-bold text-gray-900">{t.name}</p>
                  <p className="text-sm text-gray-500">{t.role} · {t.grade}</p>
                  <p className="text-xs text-[#B41C2C] font-medium">{t.campus}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={prev} className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:border-[#B41C2C] hover:text-[#B41C2C] transition-colors"><ChevronLeft size={16} /></button>
                <button onClick={next} className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:border-[#B41C2C] hover:text-[#B41C2C] transition-colors"><ChevronRight size={16} /></button>
              </div>
            </div>
          </div>
          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((_, i) => (
              <button key={i} onClick={() => setCurrent(i)} className={`rounded-full transition-all ${ i === current ? "w-6 h-2 bg-[#B41C2C]" : "w-2 h-2 bg-gray-300" }`} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== CTA SECTION ====================
const CTASection = () => (
  <section className="py-20 bg-[#87111E]">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
        Begin Your Child&apos;s Journey Today
      </h2>
      <p className="text-[#FEE7E9] text-lg mb-8 max-w-xl mx-auto">
        Join a community of 5000+ learners across 6 Bengaluru campuses. Admissions open for 2026–27.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/admissions" className="bg-white text-[#87111E] font-bold px-8 py-3.5 rounded-xl hover:bg-gray-100 transition-colors text-base">
          Apply Online Now
        </Link>
        <Link to="/contact" className="border-2 border-white/50 hover:border-white text-white font-bold px-8 py-3.5 rounded-xl hover:bg-white/10 transition-colors text-base">
          Contact Us
        </Link>
      </div>
    </div>
  </section>
);

// ==================== GALLERY SCROLL SECTION ====================
const GallerySection = () => {
  const [photos, setPhotos] = useState([]);

  const resolveUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${CRM}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  useEffect(() => {
    axios
      .get(`${CRM}/api/public/gallery/tipsblr`, { headers: { "X-API-Key": CRM_KEY } })
      .then((res) => {
        if (res.data && res.data.length > 0) {
          // Only real uploads: type="photo". Skip type="image" (CRM stock images)
          const normalized = res.data
            .filter((item) => item.type === "photo")
            .map((item) => ({ ...item, url: resolveUrl(item.url) }));
          setPhotos(normalized);
        } else {
          setPhotos([]);
        }
      })
      .catch(() => {
        setPhotos([]); // CRM error — no mock fallback
      });
  }, []);

  if (photos.length === 0) return null;

  const speed = Math.max(photos.length * 3, 25);
  const doubled = [...photos, ...photos];

  return (
    <section className="py-16 bg-white" style={{ overflow: "hidden" }}>
      <style>{`
        @keyframes gallery-marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .gallery-track {
          animation: gallery-marquee ${speed}s linear infinite;
          will-change: transform;
        }
        .gallery-track:hover { animation-play-state: paused; }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="flex items-end justify-between">
          <div>
            <span className="text-[#B41C2C] text-sm font-bold uppercase tracking-widest">Campus Moments</span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Life at TIPS
            </h2>
          </div>
          <Link
            to="/gallery"
            className="hidden sm:flex items-center gap-1 text-sm font-semibold text-[#B41C2C] hover:text-[#9B1522] transition-colors"
          >
            View All Photos <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      <div style={{ overflow: "hidden" }}>
        <div className="gallery-track flex gap-4" style={{ width: "max-content" }}>
          {doubled.map((img, i) => (
            <Link
              key={`${img.id}-${i}`}
              to="/gallery"
              className="flex-shrink-0 relative group rounded-xl overflow-hidden bg-gray-200 block"
              style={{ width: "280px", height: "200px" }}
            >
              <img
                src={img.url}
                alt={img.title || "TIPS Campus"}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                onError={(e) => { e.target.parentElement.style.display = "none"; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                <span className="text-white text-xs font-semibold">{img.title}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 sm:hidden text-center">
        <Link to="/gallery" className="text-sm font-semibold text-[#B41C2C] hover:text-[#9B1522] flex items-center justify-center gap-1">
          View All Photos <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
};

// ==================== HOME PAGE ====================
const Home = () => {
  const campusList = useCampusesHook();
  const campusNames = campusList.map((c) => c.name);
  return (
    <main>
      <SEO
        title="CBSE School Bangalore | Admissions Open 2026-27"
        description="The Indian Public School (TIPS) Bengaluru – Premier CBSE school with 6 campuses across Bangalore. Admissions open 2026–27 for Nursery to Grade XII. Book a free counselling session."
        keywords="TIPS Bengaluru, The Indian Public School Bengaluru, CBSE school Bangalore, best CBSE school Bangalore, school admissions Bangalore 2026, top CBSE school in Bangalore, TIPS school Bangalore admissions"
        canonical="/"
      />
      <HeroSection campusList={campusList} campusNames={campusNames} />
      <StatsSection />
      <AcademicsSection />
      <TipsEdgeSection />
      <CampusTourSection />
      <GallerySection />
      <CampusesSection campuses={campusList} />
      <EventsSection />
      <TestimonialsSection />
      <CTASection />
    </main>
  );
};

export default Home;